import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/statesData";
import { toast } from "sonner";
import { Eye, EyeOff, Search } from "lucide-react";

/**
 * Admin panel to enable / disable entire states.
 * Disabling a state hides it (and all its hotels, cities) from public views.
 */
export default function StateManager({ hotels = [] }) {
  const [disabledSet, setDisabledSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(new Set());

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getDisabledStates();
      setDisabledSet(new Set(res.states));
    } catch {
      toast.error("Could not load disabled states");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (state) => {
    const isDisabling = !disabledSet.has(state);
    if (isDisabling && !window.confirm(`Disable "${state}"? All hotels and cities in ${state} will be hidden from public views.`)) return;
    setPending((s) => new Set(s).add(state));
    try {
      const res = await api.toggleState(state, isDisabling);
      setDisabledSet(new Set(res.disabled_states));
      toast.success(isDisabling ? `${state} disabled` : `${state} enabled`);
    } catch {
      toast.error("Failed to update state");
    } finally {
      setPending((s) => { const n = new Set(s); n.delete(state); return n; });
    }
  };

  // Build state list with counts derived from hotels prop
  const stateCounts = useMemo(() => {
    const m = new Map();
    for (const h of hotels) {
      const c = m.get(h.state) || { count: 0, upcoming: 0 };
      c.count += 1;
      if (h.status === "upcoming") c.upcoming += 1;
      m.set(h.state, c);
    }
    return m;
  }, [hotels]);

  const list = useMemo(() => {
    const query = q.trim().toLowerCase();
    // Show states that either have hotels OR are currently disabled OR are in the master list (for pre-emptive disable)
    const withActivity = new Set([...stateCounts.keys(), ...disabledSet, ...INDIAN_STATES]);
    return [...withActivity]
      .filter((s) => !query || s.toLowerCase().includes(query))
      .sort((a, b) => {
        const aHas = (stateCounts.get(a)?.count || 0) > 0;
        const bHas = (stateCounts.get(b)?.count || 0) > 0;
        if (aHas !== bHas) return aHas ? -1 : 1;
        return a.localeCompare(b);
      });
  }, [q, stateCounts, disabledSet]);

  return (
    <div data-testid="state-manager" className="border border-[var(--ink)] bg-white">
      <div className="p-4 sm:p-5 border-b border-[var(--line)] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-label text-[10px] text-[var(--brand-blue)] mb-1">State Visibility</div>
          <h3 className="font-display text-xl sm:text-2xl">Enable or disable whole states</h3>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Disabled states are hidden from all public views (map, directory, search).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="font-label text-[9px] text-[var(--ink-soft)]">Disabled</div>
            <div className="font-display text-xl">{disabledSet.size}</div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5 border-b border-[var(--line)]">
        <div className="flex items-center gap-2 border-b-2 border-[var(--line)] focus-within:border-[var(--brand-blue)]">
          <Search className="w-3.5 h-3.5 text-[var(--ink-soft)]" />
          <input
            data-testid="state-manager-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search state name…"
            className="flex-1 bg-transparent outline-none py-2 text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-[var(--ink-soft)]">Loading states…</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          {list.map((state) => {
            const counts = stateCounts.get(state);
            const isDisabled = disabledSet.has(state);
            const isPending = pending.has(state);
            return (
              <div
                key={state}
                data-testid={`state-manager-row-${slug(state)}`}
                className={`flex items-center justify-between px-4 sm:px-5 py-3 border-b border-[var(--line)] last:border-0 ${
                  isDisabled ? "bg-red-50" : "hover:bg-[var(--paper-2)]"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base truncate">{state}</div>
                  <div className="text-xs text-[var(--ink-soft)]">
                    {counts ? (
                      <>
                        {counts.count} {counts.count === 1 ? "hotel" : "hotels"}
                        {counts.upcoming > 0 && (
                          <span className="ml-2 text-[var(--brand-orange)]">· {counts.upcoming} upcoming</span>
                        )}
                      </>
                    ) : (
                      <span>No hotels yet</span>
                    )}
                    {isDisabled && <span className="ml-2 text-red-600 font-semibold">· Hidden from public</span>}
                  </div>
                </div>
                <button
                  data-testid={`state-manager-toggle-${slug(state)}`}
                  onClick={() => toggle(state)}
                  disabled={isPending}
                  className={`ml-3 shrink-0 inline-flex items-center gap-1.5 px-3 py-2 font-label text-[10px] border-2 ${
                    isDisabled
                      ? "border-red-500 text-red-600 hover:bg-red-500 hover:text-white"
                      : "border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white"
                  } ${isPending ? "opacity-50" : ""}`}
                >
                  {isDisabled ? (<><Eye className="w-3 h-3" /> Enable</>) : (<><EyeOff className="w-3 h-3" /> Disable</>)}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
